import i18n from "../../../i18n";
import React, { useState, useEffect } from "react";
import { useFormatter } from "../../../hooks/useFomatter";
import { useServerApi } from "../../../hooks/userServerApi";
import { IconSearch } from "@tabler/icons";
import {
  Button,
  Text,
  Title,
  Badge,
  Anchor,
  LoadingOverlay,
  Box,
  Paper,
  Table,
  Grid,
  Group,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import moment from "moment";

const IncentivesTab = ({ form }) => {
  const [api] = useServerApi();
  const [formatter] = useFormatter();

  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [incentives, setIncentives] = useState([]);

  const payrollStatus = form?.values?.status;
  const isConfirmed =
    payrollStatus === "CONFIRMED" || payrollStatus === "COMPLETED";

  useEffect(() => {
    const loadSavedIncentives = async () => {
      const savedSearch = form?.values?.incentiveSearch;
      if (savedSearch) {
        if (savedSearch.fromDate) setFromDate(new Date(savedSearch.fromDate));
        if (savedSearch.toDate) setToDate(new Date(savedSearch.toDate));

        try {
          setLoading(true);
          const result = await api.Payroll2.getIncentivesForPayroll(
            moment(savedSearch.fromDate).format("YYYY-MM-DD"),
            moment(savedSearch.toDate).format("YYYY-MM-DD"),
            null,
          );

          if (result?.success && result.data?.incentives) {
            setIncentives(result.data.incentives);
          }
        } catch (error) {
          console.log("Error loading saved incentives:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadSavedIncentives();
  }, [form?.values?._id]);

  const saveIncentivesToPayroll = async (incentiveIds) => {
    if (!form?.values?._id) return;
    try {
      await api.updateById({
        apiEntity: "payroll",
        id: form.values._id,
        values: {
          incentives: incentiveIds,
          incentiveSearch: {
            fromDate,
            toDate,
          },
        },
      });
    } catch (error) {
      console.log("Error saving incentives to payroll:", error);
    }
  };

  const handleSearch = async () => {
    if (!fromDate || !toDate) {
      return;
    }

    try {
      setLoading(true);
      const result = await api.Payroll2.getIncentivesForPayroll(
        moment(fromDate).format("YYYY-MM-DD"),
        moment(toDate).format("YYYY-MM-DD"),
        form?.values?._id,
      );

      if (result && result.success && result.data && result.data.incentives) {
        setIncentives(result.data.incentives);
        const incentiveIds = result.data.incentives.map((i) => i._id);
        await saveIncentivesToPayroll(incentiveIds);
        console.log(
          `Loaded ${result.data.count} incentives (no pagination limit)`,
        );
      } else {
        setIncentives([]);
      }
    } catch (error) {
      console.log("Error fetching incentives:", error);
      setIncentives([]);
    } finally {
      setLoading(false);
    }
  };

  const totalIncentives = incentives.filter((i) => (i.amount || 0) >= 0);
  const totalCharges = incentives.filter((i) => (i.amount || 0) < 0);
  const totalIncentiveAmount = totalIncentives.reduce(
    (sum, i) => sum + (i.amount || 0),
    0,
  );
  const totalChargeAmount = totalCharges.reduce(
    (sum, i) => sum + Math.abs(i.amount || 0),
    0,
  );
  const netAmount = totalIncentiveAmount - totalChargeAmount;

  return (
    <Box pos="relative">
      <LoadingOverlay visible={loading} overlayBlur={2} />

      <Paper p="md" withBorder mb="lg">
        <Title order={5} mb="md">
          Search Incentive / Charge by Issue Date
        </Title>

        <Group align="flex-end" spacing="md">
          <DatePicker
            label="From Date"
            size="xs"
            placeholder="Select start date"
            value={fromDate}
            onChange={setFromDate}
            allowFreeInput
            clearable
            disabled={isConfirmed}
          />

          <DatePicker
            label="To Date"
            size="xs"
            placeholder="Select end date"
            value={toDate}
            onChange={setToDate}
            allowFreeInput
            clearable
            disabled={isConfirmed}
          />

          <Button
            size="xs"
            leftIcon={<IconSearch size={14} />}
            onClick={handleSearch}
            disabled={!fromDate || !toDate || isConfirmed}
          >{i18n.t("Search")}</Button>
        </Group>
        {isConfirmed && (
          <Text size="xs" color="orange" mt="xs">
            Payroll is completed. No more edits allowed.
          </Text>
        )}
      </Paper>

      {incentives.length > 0 && (
        <>
          <Grid mb="md">
            <Grid.Col span={3}>
              <Paper p="md" withBorder>
                <Text size="xs" color="dimmed">
                  TOTAL RECORDS
                </Text>
                <Title order={3}>{incentives.length}</Title>
              </Paper>
            </Grid.Col>
            <Grid.Col span={3}>
              <Paper p="md" withBorder>
                <Text size="xs" color="dimmed">
                  INCENTIVES ({totalIncentives.length})
                </Text>
                <Title order={3} color="green">
                  {formatter.currency(totalIncentiveAmount)}
                </Title>
              </Paper>
            </Grid.Col>
            <Grid.Col span={3}>
              <Paper p="md" withBorder>
                <Text size="xs" color="dimmed">
                  CHARGES ({totalCharges.length})
                </Text>
                <Title order={3} color="red">
                  {formatter.currency(totalChargeAmount)}
                </Title>
              </Paper>
            </Grid.Col>
            <Grid.Col span={3}>
              <Paper p="md" withBorder>
                <Text size="xs" color="dimmed">
                  NET AMOUNT
                </Text>
                <Title order={3} color={netAmount >= 0 ? "blue" : "red"}>
                  {formatter.currency(netAmount)}
                </Title>
              </Paper>
            </Grid.Col>
          </Grid>

          <Paper p="md" withBorder>
            <Table fontSize="xs" striped highlightOnHover>
              <thead>
                <tr>
                  <th>#</th>
                  <th>{i18n.t("Code")}</th>
                  <th>{i18n.t("Type")}</th>
                  <th>{i18n.t("Title")}</th>
                  <th>{i18n.t("Amount")}</th>
                  <th>{i18n.t("Consultant")}</th>
                  <th>{i18n.t("Issue Date")}</th>
                  <th>{i18n.t("Remark")}</th>
                </tr>
              </thead>
              <tbody>
                {incentives.map((incentive, index) => (
                  <tr key={incentive._id}>
                    <td>{index + 1}</td>
                    <td>
                      <Anchor
                        href={`/operation/incentive?id=${incentive._id}&mode=edit`}
                        target="_blank"
                        size="xs"
                      >
                        {incentive.code}
                      </Anchor>
                    </td>
                    <td>
                      <Badge
                        color={(incentive.amount || 0) >= 0 ? "green" : "red"}
                        size="sm"
                      >
                        {(incentive.amount || 0) >= 0 ? "INCENTIVE" : "CHARGE"}
                      </Badge>
                    </td>
                    <td>
                      <Text size="xs" lineClamp={2} maw={200}>
                        {incentive.title || "-"}
                      </Text>
                    </td>
                    <td>
                      <Text
                        size="xs"
                        weight={500}
                        color={(incentive.amount || 0) >= 0 ? "green" : "red"}
                      >
                        {formatter.currency(incentive.amount)}
                      </Text>
                    </td>
                    <td>
                      {incentive.consultant ? (
                        <Box>
                          <Text size="xs" weight={500}>
                            {incentive.consultant.staffId || "-"}
                          </Text>
                          <Text size="xs" color="dimmed">
                            {incentive.consultant.name ||
                              incentive.consultant.cname ||
                              "-"}
                          </Text>
                        </Box>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      {incentive.issueDate
                        ? moment(incentive.issueDate).format("YYYY-MM-DD")
                        : "-"}
                    </td>
                    <td>
                      <Text size="xs" lineClamp={1} maw={150}>
                        {incentive.remark || "-"}
                      </Text>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Paper>
        </>
      )}

      {incentives.length === 0 && !loading && (
        <Paper p="xl" withBorder>
          <Text color="dimmed" align="center">
            No incentives or charges found. Please enter date range and click
            Search.
          </Text>
        </Paper>
      )}
    </Box>
  );
};

export default IncentivesTab;
